// src/pages/PrincipalRegistration.js
// v7 – success-screen app install handoff
//
// Changes vs v6:
// ✅ Adds smart post-registration App install guidance
// ✅ Shows direct install button on phones/tablets
// ✅ Shows QR-code phone handoff on desktop/laptop
// ✅ Keeps registration-only scope (no onboarding fields here)

import React, { useState, useEffect, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";

import api from "../services/api";
import { API_PATHS, MOBILE_APP_INSTALL_URL } from "../config";
import "../styles/PrincipalRegistration.css";

const normalizeRollNumber = (value) => String(value || "").trim().replace(/\s+/g, "").toUpperCase();

const normalizeWebsite = (value) => {
  const cleaned = String(value || "").trim();
  if (!cleaned) return "";
  if (/^https?:\/\//i.test(cleaned)) return cleaned;
  return `https://${cleaned}`;
};

const isValidWebsite = (value) => {
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) && String(url.hostname || "").includes(".");
  } catch {
    return false;
  }
};

const isValidEmail = (value) => /\S+@\S+\.\S+/.test(String(value || "").trim());

const hasText = (value) => String(value || "").trim().length > 0;

const isLikelyMobileInstallDevice = () => {
  if (typeof navigator === "undefined") return false;

  if (typeof navigator.userAgentData?.mobile === "boolean") {
    return navigator.userAgentData.mobile;
  }

  const ua = String(navigator.userAgent || "");
  if (/Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(ua)) return true;

  // iPadOS can identify itself as Macintosh while still being a touch device.
  return /Macintosh/i.test(ua) && Number(navigator.maxTouchPoints || 0) > 1;
};

const getRegistrationSource = () => {
  if (typeof window === "undefined") return "";

  try {
    return String(new URLSearchParams(window.location.search).get("source") || "")
      .trim()
      .toLowerCase();
  } catch {
    return "";
  }
};

const PrincipalRegistration = () => {
  const [rollNumber, setRollNumber] = useState("");
  const [schoolData, setSchoolData] = useState(null);
  const [schoolLookupLoading, setSchoolLookupLoading] = useState(false);
  const [schoolLookupRoll, setSchoolLookupRoll] = useState("");

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [principalEmail, setPrincipalEmail] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isMobileInstallDevice, setIsMobileInstallDevice] = useState(false);

  const [schoolWebsite, setSchoolWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");

  const [prefilledEmail, setPrefilledEmail] = useState("");
  const [prefilledWebsite, setPrefilledWebsite] = useState("");
  const [prefilledDisplayName, setPrefilledDisplayName] = useState("");

  const [emailTouched, setEmailTouched] = useState(false);
  const [websiteTouched, setWebsiteTouched] = useState(false);
  const [displayNameTouched, setDisplayNameTouched] = useState(false);

  const isFromDemoApp = useMemo(() => getRegistrationSource() === "demo_app", []);

  useEffect(() => {
    if (message) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [message]);

  useEffect(() => {
    setIsMobileInstallDevice(isLikelyMobileInstallDevice());
  }, []);

  const setErrorMessage = (text) => {
    setMessage(text);
    setMessageType("error");
  };

  const clearMessage = () => {
    setMessage("");
    setMessageType("");
  };

  const clearSchoolSelection = () => {
    setSchoolData(null);
    setSchoolLookupRoll("");
    setPrincipalEmail("");
    setEmail("");
    setDisplayName("");
    setSchoolWebsite("");
    setPrefilledEmail("");
    setPrefilledWebsite("");
    setPrefilledDisplayName("");
    setEmailTouched(false);
    setWebsiteTouched(false);
    setDisplayNameTouched(false);
  };

  const fetchSchoolData = async () => {
    if (schoolLookupLoading || submitting) return;

    const cleanedRollNumber = normalizeRollNumber(rollNumber);
    setRollNumber(cleanedRollNumber);

    if (!cleanedRollNumber) {
      clearSchoolSelection();
      setErrorMessage("Please enter your school roll number.");
      return;
    }

    if (cleanedRollNumber.length < 5) {
      clearSchoolSelection();
      setErrorMessage("Please enter the full school roll number.");
      return;
    }

    setSchoolLookupLoading(true);
    clearMessage();

    try {
      const { data } = await api.get(API_PATHS.GET_SCHOOL_DATA(cleanedRollNumber));
      setSchoolData(data || null);
      setSchoolLookupRoll(cleanedRollNumber);

      const nextEmail = String(data?.principal_email || "").trim();
      const nextWebsite = normalizeWebsite(data?.school_website || "");
      const nextDisplayName = String(data?.principal_name || "").trim();

      setPrincipalEmail(nextEmail);
      setEmail(nextEmail);
      setDisplayName(nextDisplayName);
      setSchoolWebsite(nextWebsite);

      setPrefilledEmail(nextEmail);
      setPrefilledWebsite(nextWebsite);
      setPrefilledDisplayName(nextDisplayName);

      setEmailTouched(false);
      setWebsiteTouched(false);
      setDisplayNameTouched(false);
      clearMessage();
    } catch (error) {
      clearSchoolSelection();
      setErrorMessage(error?.response?.data?.detail || "School not found. Please check the roll number.");
    } finally {
      setSchoolLookupLoading(false);
    }
  };

  const handleRegister = async (event) => {
    event?.preventDefault?.();
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (submitting) return;

    const cleanedRollNumber = normalizeRollNumber(rollNumber);
    const normalizedWebsite = normalizeWebsite(schoolWebsite);
    const normalizedEmail = String(email || "").trim();
    const normalizedName = String(displayName || "").trim();

    if (!schoolData || schoolLookupRoll !== cleanedRollNumber) {
      setErrorMessage("Please find and confirm your school before continuing.");
      return;
    }

    if (!isValidEmail(normalizedEmail)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    if (!normalizedName) {
      setErrorMessage("Please enter your name.");
      return;
    }

    if (!normalizedWebsite) {
      setErrorMessage("Please enter your school website.");
      return;
    }

    if (!isValidWebsite(normalizedWebsite)) {
      setErrorMessage("Please enter a valid school website, for example https://www.yourschool.ie.");
      return;
    }

    const payload = {
      email: normalizedEmail,
      display_name: normalizedName,
      role: "PRINCIPAL",
      roll_number: cleanedRollNumber,
      school_website: normalizedWebsite,
    };

    setRollNumber(cleanedRollNumber);
    setSchoolWebsite(normalizedWebsite);
    setSubmitting(true);
    clearMessage();

    try {
      await api.post(API_PATHS.PRINCIPAL_REGISTRATION, payload);
      clearMessage();
      setIsRegistered(true);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setErrorMessage(typeof detail === "string" ? detail : "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const emailIsPrefilled = useMemo(() => {
    if (!prefilledEmail) return false;
    if (emailTouched) return false;
    return email === prefilledEmail;
  }, [email, prefilledEmail, emailTouched]);

  const websiteIsPrefilled = useMemo(() => {
    if (!prefilledWebsite) return false;
    if (websiteTouched) return false;
    return schoolWebsite === prefilledWebsite;
  }, [schoolWebsite, prefilledWebsite, websiteTouched]);

  const displayNameIsPrefilled = useMemo(() => {
    if (!prefilledDisplayName) return false;
    if (displayNameTouched) return false;
    return displayName === prefilledDisplayName;
  }, [displayName, prefilledDisplayName, displayNameTouched]);

  const schoolAddress = [schoolData?.address_line_1, schoolData?.county_description]
    .filter(hasText)
    .join(", ");

  const canSubmit = !!(
    schoolData &&
    schoolLookupRoll === normalizeRollNumber(rollNumber) &&
    email &&
    displayName.trim() &&
    schoolWebsite.trim()
  );

  if (isRegistered) {
    return (
      <div className="principal-registration page">
        <div className="registration-card">
          <header className="registration-header">
            <h1 className="registration-title">Check your email</h1>
            <p className="registration-subtitle">
              We&apos;re setting things up for your school.
            </p>
          </header>

          {isFromDemoApp && (
            <section className="demo-trial-card" aria-label="Demo trial handoff confirmation">
              <p className="demo-trial-eyebrow">Public demo handoff</p>
              <h2 className="demo-trial-title">Your real school trial will be separate</h2>
              <p className="demo-trial-text">
                The demo app is only a showroom. Your school workspace, photos, staff,
                parents, and settings will be created separately after approval.
              </p>
            </section>
          )}

          <p className="message success" aria-live="polite">
            We&apos;ll email you when you&apos;re ready to continue.
          </p>

          <section className="install-handoff-card" aria-labelledby="install-app-title">
            <div className="install-handoff-copy">
              <h2 id="install-app-title" className="install-handoff-title">
                Install the App while you wait
              </h2>
              <p className="install-handoff-text">
                When your registration is approved, open the approval email on the same phone
                where the GhrianGhraffy App is installed.
              </p>
            </div>

            {isMobileInstallDevice ? (
              <a className="install-primary-link" href={MOBILE_APP_INSTALL_URL}>
                Install the App on this phone
              </a>
            ) : (
              <div className="install-qr-wrap">
                <div className="install-qr-box" aria-label="QR code to install the GhrianGhraffy App">
                  <QRCodeSVG
                    value={MOBILE_APP_INSTALL_URL}
                    size={148}
                    bgColor="transparent"
                    fgColor="#00ffaf"
                    level="M"
                    includeMargin={false}
                  />
                </div>
                <p className="install-qr-caption">
                  Scan this QR code with your phone. Do not open the install link on this laptop.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="principal-registration page">
      <form className="registration-card" onSubmit={handleRegister} noValidate>
        <header className="registration-header">
          <h1 className="registration-title">Get started with your school</h1>
          <p className="registration-subtitle">
            Enter your school roll number, then confirm the details we find.
          </p>
        </header>

        {isFromDemoApp && (
          <section className="demo-trial-card" aria-label="Demo trial handoff">
            <p className="demo-trial-eyebrow">Public demo handoff</p>
            <h2 className="demo-trial-title">Start a free trial for your own school</h2>
            <p className="demo-trial-text">
              This creates a real school workspace for you. It will not use the public
              demo school, demo users, demo photos, or demo settings.
            </p>
          </section>
        )}

        {message && (
          <p className={`message ${messageType || "error"}`} aria-live="polite">
            {message}
          </p>
        )}

        <section className="registration-section">

          <div className="form-group">
            <label htmlFor="roll-number">Roll number</label>
            <div className="lookup-row">
              <input
                id="roll-number"
                type="text"
                className="input-field input-field--lookup"
                placeholder="e.g. 12345A"
                value={rollNumber}
                onChange={(e) => {
                  setRollNumber(e.target.value.toUpperCase());
                  if (schoolData) clearSchoolSelection();
                  if (message) clearMessage();
                }}
                onBlur={(e) => setRollNumber(normalizeRollNumber(e.target.value))}
                autoComplete="off"
              />
              <button
                type="button"
                className="lookup-button"
                onClick={fetchSchoolData}
                disabled={schoolLookupLoading || submitting || !rollNumber.trim()}
              >
                {schoolLookupLoading ? "Finding…" : "Find school"}
              </button>
            </div>
            <span className="tooltip">Use your Department of Education roll number.</span>
          </div>

          {schoolData && (
            <div className="school-details">
              {hasText(schoolData.official_name) && (
                <p>
                  <strong>School Name:</strong> {schoolData.official_name}
                </p>
              )}
              {hasText(schoolAddress) && (
                <p>
                  <strong>Address:</strong> {schoolAddress}
                </p>
              )}
              {hasText(schoolData.eircode) && (
                <p>
                  <strong>Eircode:</strong> {schoolData.eircode}
                </p>
              )}
              {hasText(schoolData.phone_no) && (
                <p>
                  <strong>Phone:</strong> {schoolData.phone_no}
                </p>
              )}
              {hasText(schoolData.principal_name) && (
                <p>
                  <strong>Principal:</strong> {schoolData.principal_name}
                </p>
              )}
              {hasText(principalEmail) && (
                <p>
                  <strong>Principal Email:</strong> {principalEmail}
                </p>
              )}
              {hasText(schoolData.enrolment_quantity) && (
                <p>
                  <strong>Enrolment:</strong> {schoolData.enrolment_quantity}
                </p>
              )}
              {hasText(schoolData.school_website) && (
                <p>
                  <strong>Website on file:</strong>{" "}
                  <a
                    className="school-website-link"
                    href={normalizeWebsite(schoolData.school_website)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {normalizeWebsite(schoolData.school_website)}
                  </a>
                </p>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="school-website">School website</label>
            <input
              id="school-website"
              type="url"
              className={`input-field ${websiteIsPrefilled ? "is-prefilled" : ""}`}
              placeholder="e.g. https://www.yourschool.ie"
              value={schoolWebsite}
              onChange={(e) => {
                if (!websiteTouched) setWebsiteTouched(true);
                setSchoolWebsite(e.target.value);
              }}
              onBlur={(e) => setSchoolWebsite(normalizeWebsite(e.target.value))}
              required
            />
            <span className="tooltip">Edit this only if the website on file is incorrect.</span>
          </div>
        </section>

        <section className="registration-section">

          <div className="form-group">
            <label htmlFor="display-name">Your name</label>
            <input
              id="display-name"
              type="text"
              className={`input-field ${displayNameIsPrefilled ? "is-prefilled" : ""}`}
              placeholder="e.g. Mary Whelan"
              value={displayName}
              onChange={(e) => {
                if (!displayNameTouched) setDisplayNameTouched(true);
                setDisplayName(e.target.value);
              }}
              required
            />
            <span className="tooltip">You can change this if the name on file is incorrect.</span>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className={`input-field ${emailIsPrefilled ? "is-prefilled" : ""}`}
              placeholder="e.g. principal@school.ie"
              value={email}
              onChange={(e) => {
                if (!emailTouched) setEmailTouched(true);
                setEmail(e.target.value);
              }}
              required
            />
            <span className="tooltip">Use the email address that&apos;s easiest for you to access.</span>
          </div>
        </section>

        <button
          type="submit"
          className="register-button"
          disabled={submitting || schoolLookupLoading || !canSubmit}
        >
          {submitting ? "Submitting…" : "Continue"}
        </button>
      </form>
    </div>
  );
};

export default PrincipalRegistration;